import React, { useState, useEffect } from 'react'
import { Menu, Home, LogOut, User } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { deleteCookie, getCookie } from '../app/axios/CookieConfig'
import { toast } from 'sonner'
import { useRole } from '../context/RoleContext'

const Navbar = ({ toggleSidebar, sidebarCollapsed }) => {
  const { setRole } = useRole()
  const profileEmail = getCookie('email')
  const profileName = getCookie('name')
  const profileRole = getCookie('role')

  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10
      setScrolled(isScrolled)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleHomeClick = () => {
    navigate('/')
  }

  const logOut = () => {
    deleteCookie('token')
    setRole(null)
    toast.success('Logout Successfully!')
    navigate('/login')
  }

  return (
    <nav
      // *** MODIFIED FOR RESPONSIVENESS ***
      className={`fixed top-0 right-0 z-40 transition-all duration-300 left-0 
      ${sidebarCollapsed ? 'lg:left-20' : 'lg:left-72'} 
      ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200/50'
          : 'bg-white shadow-sm border-b border-gray-100'
      }`}
    >
      <div className='px-4 sm:px-6 lg:px-8'>
        <div className='flex justify-between items-center h-16'>
          {/* Left Section - Mobile Menu & Search */}
          <div className='flex items-center space-x-4'>
            {/* Hamburger Menu - Always shows on mobile, hidden on desktop */}
            <button
              onClick={toggleSidebar}
              className='lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-all duration-200'
            >
              <Menu className='w-6 h-6' />
            </button>

            {/* Search Bar */}
            {/* <div className='hidden md:flex relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <Search className='h-4 w-4 text-gray-400' />
              </div>
              <input
                type='text'
                placeholder='Search students, fees...'
                disabled
                className='pl-10 pr-4 py-2 w-64 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white'
              />
            </div> */}
            <h1 className='hidden md:block text-lg font-bold text-blue-700'>
              Inflection Dashboard
            </h1>
          </div>

          {/* Center Section - Title (Mobile) */}
          <div className='md:hidden'>
            <h1 className='text-lg font-semibold text-gray-800'>Dashboard</h1>
          </div>

          {/* Right Section - Actions */}
          <div className='flex items-center space-x-3'>
            <button
              onClick={handleHomeClick}
              className='hidden md:flex items-center space-x-2 px-4 py-2 rounded-lg text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200 font-medium'
            >
              <Home className='w-4 h-4' />
              <span>Home</span>
            </button>

            {/* <div className='relative'>
              <button className='p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-all duration-200 relative'>
                <Bell className='w-5 h-5' />
                <span className='absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center'>
                  3
                </span>
              </button>
            </div> */}

            <div className='relative'>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className='flex items-center space-x-2 p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-all duration-200'
              >
                <div className='w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center'>
                  <User className='w-4 h-4 text-white' />
                </div>
                <div className='hidden md:block text-left'>
                  <p className='text-sm font-medium text-gray-800'>
                    {profileRole}
                  </p>
                  <p className='text-xs text-gray-500'>{profileName}</p>
                </div>
              </button>
              {showUserMenu && (
                <>
                  <div
                    className='fixed inset-0 z-10'
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div className='absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 z-20 overflow-hidden'>
                    <div className='p-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white'>
                      <div className='flex items-center space-x-3'>
                        <div className='w-10 h-10 bg-white/20 rounded-full flex items-center justify-center'>
                          <User className='w-5 h-5' />
                        </div>
                        <div>
                          <p className='font-medium'>{profileRole}</p>
                          <p className='text-sm text-white/80'>
                            {profileEmail}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className='p-2'>
                      <button
                        onClick={handleHomeClick}
                        className='flex items-center space-x-3 w-full p-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-200'
                      >
                        <Home className='w-4 h-4 text-gray-500' />
                        <span>Dashboard</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false)
                        }}
                        className='flex items-center space-x-3 w-full p-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-200'
                      >
                        <User className='w-4 h-4 text-gray-500' />
                        <span>Profile Settings</span>
                      </button>
                      <hr className='my-2 border-gray-200' />
                      <button
                        onClick={logOut}
                        className='flex items-center space-x-3 w-full p-3 text-left text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200'
                      >
                        <LogOut className='w-4 h-4' />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* <button className='md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-all duration-200'>
              <Search className='w-5 h-5' />
            </button> */}
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
